import { useCallback, useEffect, useMemo, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "../../../components/forms/form/Form";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import SaveAsDialog, { bindSaveAsDialog, useSaveAsDialogState } from "../../../components/save-as-dialog/SaveAsDialog";
import StepMenu, { StepDefinition } from "../../../components/step-menu/StepMenu";
import { StepMenuEditModeEnum, useStepMenuState } from "../../../components/step-menu/StepMenuState";
import UnsavedChangesPrompt from "../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useCheckPresetExists } from "../../../queries/ui-customization/UseCheckPresetExists";
import { useSaveUICustomizationPreset } from "../../../queries/ui-customization/UseSaveUICustomizationPreset";
import { useUICustomizationPreset } from "../../../queries/ui-customization/UseUICustomizationPreset";
import i18n from "../../../services/localization-service/i18n";
import { IUICustomizationPresetDTO } from "../../../services/ui-customization-data-service/Models";
import { StepEnum } from "./StepEnum";
import { generalIsValid, membershipIsValid, mergePreset, NewPresetID, pageVisibilityIsValid, presetFieldIsDirty, toPresetField } from "./UICustomizationPresetEditorEditorUtils";
import { UICustomizationPresetAssociatedUserFieldType, UICustomizationPresetFieldType } from "./UICustomizationPresetEditorFieldTypes";
import GeneralStep from "./general-step/GeneralStep";
import MembershipStep from "./membership-step/MembershipStep";
import PageVisibilityStep from "./page-visibility-step/PageVisibilityStep";

export type UICustomizationPresetEditorCloseType = {
    reason: "cancel" | "saveAsNew" | "saveChanges";
    presetID: number | undefined;
};

export type UICustomizationPresetEditorProps = {
    presetID: number | undefined;
    organizationID: number | undefined;
    onClose: ({ reason, presetID }: UICustomizationPresetEditorCloseType) => void;
};

export default function UICustomizationPresetEditor({
    presetID,
    organizationID,
    onClose,
}: UICustomizationPresetEditorProps): JSX.Element {

    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const saveAsDialog = useSaveAsDialogState();

    const { data: presetModel, status: presetStatus } = useQueryNotifications(useUICustomizationPreset)({
        groupID: routeContext.groupID as number,
        presetID: presetID as number
    });

    const { mutateAsync: savePreset } = useQueryNotifications(useSaveUICustomizationPreset)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        onSuccess: (data) => {
            // save success            
            acknowledge(t("uiCustomizationPresetEditor.saveSuccessMessage", data));
        }
    });

    const { mutateAsync: checkPresetExists } = useQueryNotifications(useCheckPresetExists)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number
    });

    const initValue: UICustomizationPresetFieldType = {
        id: NewPresetID,
        name: undefined,
        description: undefined,
        users: new Array<UICustomizationPresetAssociatedUserFieldType>(),
        customizationSettings: undefined
    };

    const form = useForm<UICustomizationPresetFieldType>({
        mode: "all",
        defaultValues: initValue
    });

    const {
        getValues,
        formState: {
            dirtyFields
        },
        reset,
        setValue,
        control,
        handleSubmit
    } = form;

    const editMode = presetID === NewPresetID
        ? StepMenuEditModeEnum.Create
        : StepMenuEditModeEnum.Edit;

    const preset = useWatch({ control: control }) as UICustomizationPresetFieldType;

    const isGeneralValid = generalIsValid(preset);
    const isPageVisibilityValid = isGeneralValid && pageVisibilityIsValid(/*preset*/);
    const isMembershipValid = isPageVisibilityValid && membershipIsValid(/*preset*/);

    const isGeneralEnabled = true;
    const isPageVisibilityEnabled = isGeneralEnabled && isGeneralValid;
    const isMembershipEnabled = isPageVisibilityEnabled && isPageVisibilityValid;

    const stepDefinitions: Array<StepDefinition> = useMemo(() => {

        return [{
            key: StepEnum.General,
            title: i18n.t("uiCustomizationPresetEditor.steps.general.title"),
            disabled: !isGeneralEnabled,
            valid: isGeneralValid
        }, {
            key: StepEnum.PageVisibility,
            title: i18n.t("uiCustomizationPresetEditor.steps.pageVisibility.title"),
            disabled: !isPageVisibilityEnabled,
            valid: isPageVisibilityValid
        }, {
            key: StepEnum.Membership,
            title: i18n.t("uiCustomizationPresetEditor.steps.membership.title"),
            disabled: !isMembershipEnabled,
            valid: isMembershipValid
        }];
    }, [
        isGeneralValid,
        isPageVisibilityValid,
        isMembershipValid,
        isGeneralEnabled,
        isPageVisibilityEnabled,
        isMembershipEnabled
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveAsNewButtonState,
            saveChangesButtonState
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveAsNewButtonDisabled,
        setSaveAsNewButtonVisible,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
    } = useStepMenuState(
        editMode,
        undefined,
        stepDefinitions
    );

    // A separate isDirty from the one returned by useForm is implemented
    // here so we can exclude the expanded and formID fields from the data.
    const isDirty = presetFieldIsDirty(dirtyFields);

    const updateButtonStates = useCallback(() => {

        switch (activeStep) {

            case StepEnum.General:
                setNextButtonDisabled(!(isPageVisibilityEnabled));
                break;

            case StepEnum.PageVisibility:
                setNextButtonDisabled(!(isMembershipEnabled));
                break;

            case StepEnum.Membership:
                setNextButtonDisabled(false);

                // Override the step menu state default button changes
                if (editMode) {
                    setSaveAsNewButtonVisible(false);
                    setSaveChangesButtonVisible(true);
                    setSaveChangesButtonDisabled(!isDirty);
                } else {
                    setSaveChangesButtonVisible(false);
                    setSaveAsNewButtonVisible(true);
                    setSaveAsNewButtonDisabled(!isDirty);
                }

                break;
        }
    }, [
        editMode,
        setNextButtonDisabled,
        setSaveAsNewButtonDisabled,
        setSaveChangesButtonDisabled,
        setSaveAsNewButtonVisible,
        setSaveChangesButtonVisible,
        activeStep,
        isDirty,
        isPageVisibilityEnabled,
        isMembershipEnabled
    ]);

    // #region Effects

    // This effect updates the form values after data is retrieved. Want to keep this
    // simple with minimal dependencies, otherwise rendering problems may ensue!
    useEffect(() => {
        if (presetModel) {
            // We have our preset now, so we need to convert it to
            // values for react hook form.
            const preset//: PresetFieldType 
                = toPresetField(presetModel);

            // Reset the form with the preset's values
            reset(preset);
        }
    }, [
        presetModel,
        reset
    ]);

    // This effect updates step menu button states whenever the preset form values change
    useEffect(() => {

        updateButtonStates();
    }, [preset, updateButtonStates]);

    // #endregion Effects

    // #region Event Handlers

    async function onCancelEdit() {

        if (!isDirty || (await getUserExitConfirmation())) {
            onClose({ reason: "cancel", presetID: undefined });
        }
    }

    function onCancelStep() {
    }

    async function onStepChange(fromStep: number, toStep: number) {
        if (fromStep === StepEnum.General) {
            if (dirtyFields?.name && preset.name) {
                await checkPresetExists({ presetName: preset.name });
            }
            setActiveStep(toStep);
        } else {
            setActiveStep(toStep);
        }
    }

    async function onSaveAsNew() {
        const preset = getValues();

        const savedPreset = await save({
            ...preset,
            id: NewPresetID,
        });

        onClose({
            reason: "saveAsNew",
            presetID: savedPreset?.id
        });
    }

    async function onSaveChanges() {

        const preset = getValues();

        const savedPreset = await save(preset);

        onClose({
            reason: "saveChanges",
            presetID: savedPreset?.id
        });
    }

    // #endregion Event Handlers

    async function save(presetField: UICustomizationPresetFieldType): Promise<IUICustomizationPresetDTO | undefined> {

        if (presetModel && presetField) {
            // Merge all of our changes with the original preset
            const model = mergePreset(presetModel, presetField);

            // Save it and return the saved preset with updated ID, if applicable
            return await savePreset(model);
        }

        return undefined;
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    return (
        <>
            <LoadingZone isLoading={presetStatus === "loading"}>
                <Form form={form} sx={{ padding: 0 }}>
                    <StepMenu
                        landing={landing}
                        steps={steps}
                        title={editMode ? "Edit Preset" : "New Preset"}
                        nonLinear={true}
                        activeStep={activeStep}
                        cancelButtonState={cancelButtonState}
                        prevButtonState={prevButtonState}
                        nextButtonState={nextButtonState}
                        saveAsNewButtonState={saveAsNewButtonState}
                        saveChangesButtonState={saveChangesButtonState}
                        onCancel={onCancelEdit}
                        onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                        onSaveChanges={handleSubmit(() => onSaveChanges())}
                        onCancelStep={onCancelStep}
                        onStepChange={onStepChange}
                        onStepClick={step => onStepChange(activeStep, step.key)}
                    >
                        {activeStep === StepEnum.General &&
                            <GeneralStep />
                        }

                        {activeStep === StepEnum.PageVisibility &&
                            <PageVisibilityStep />
                        }

                        {activeStep === StepEnum.Membership &&
                            <MembershipStep organizationID={organizationID} />
                        }
                    </StepMenu>
                </Form>
            </LoadingZone >

            <SaveAsDialog {...bindSaveAsDialog(saveAsDialog)} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </>
    );
}